import withRouter from "@common/withRouter";
import SuccessPopup from "@components/PopupModals/SuccessPopup";
import { errorToast } from "@components/Toaster";
import { FeedbackQuestionBlock } from "@components/FeedbackSurvey";
import { IRouterProps } from "@Interface/Login";
import { t } from "@utils/translations";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import PageHeader from "@components/PageHeader";
import Constants from "@utils/constants";
import useApiQuery from "@hooks/useApiQuery";
import { selectors } from "@redux/index";
import { useTypedSelector } from "@redux/store";
import { shallowEqual } from "react-redux";
import useApiMutation from "@hooks/useApiMutation";
import storeErrorLog from "@utils/storeErrorLogs";
import Button from "@components/Button";
import EmptyIcon from "@assets/images/no-data.png";
import { pushToDataLayer } from "@utils/gtm";
import { DataLayerExternalKeys, DataLayerPoints } from '@common/commonDatalayerKeys';

export interface FeedbackOption {
  twFeedbackOptionId: number;
  twOption: string;
  enOption: string;
  optionId: number;
  isTextInput: boolean;
}

export interface FeedbackQuestion {
  twFeedbackQuestionId: number;
  twQuestion: string;
  enQuestion: string;
  sortOrder?: number;
  questionType: 'single' | 'multiple';
  options: FeedbackOption[];
}

export interface FeedbackSelection {
  twFeedbackOptionIds: number[];
  optionText?: string;
}

export interface GetFeedbackQuestionsResponse {
  success: boolean;
  data?: FeedbackQuestion[];
  message?: string;
}

const Feedback: React.FC<IRouterProps> = ({ router }) => {
  const [selections, setSelections] = useState<Record<number, FeedbackSelection>>({});
  const [successPopup, setSuccessPopup] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const [lineUrl, setLineUrl] = useState('');
  const feedbackStartedTracked = useRef(false);
  const dataLayerExternalKeys = DataLayerExternalKeys();
  const dataLayerPoints = DataLayerPoints();
  const missionMasterId = router?.location?.state?.missionMasterId as number;
  const campaignMasterId = Number(import.meta.env.VITE_CAMPAIGN_MASTER_ID);
  const { loggedInDetails } = useTypedSelector((state) => ({
    loggedInDetails: selectors.loggedInDetails(state)
  }), shallowEqual);

  const {
    data: feedbackData,
    isFetching: isFeedbackLoading,
    isFetched: isFeedbackFetched,
  } = useApiQuery<GetFeedbackQuestionsResponse>({
    endpoint: Constants.GET_FEEDBACK_QUESTIONS,
    queryKey: ["feedbackQuestions"],
    params: {
      memberId: loggedInDetails?.memberId,
      campaignMasterId: campaignMasterId,
      missionMasterId: missionMasterId,
    },
  });

  const submitFeedbackMutation = useApiMutation({
    endpoint: Constants.SUBMIT_FEEDBACK_QUESTIONS,
    method: 'POST',
  });

  const { isPending: isSubmittingFeedback } = submitFeedbackMutation;

  const questions: FeedbackQuestion[] = useMemo(() => {
    if (feedbackData?.success && feedbackData.data?.length) {
      return feedbackData.data;
    }
    return [];
  }, [feedbackData]);

  const noData =
    isFeedbackFetched &&
    !isFeedbackLoading &&
    (!feedbackData?.success || !feedbackData.data?.length);

  useEffect(() => {
    if (!missionMasterId) {
      router.navigate('/home');
    } else {
      submitDatalayerEvent('page_view');
    }
  }, [missionMasterId, router]);

  useEffect(() => {
    if (!isFeedbackFetched || isFeedbackLoading || !noData) return;
    errorToast(feedbackData?.message ?? t("feedback.fetchError"));
  }, [isFeedbackFetched, isFeedbackLoading, noData, feedbackData?.message]);

  useEffect(() => {
    if (!questions.length) {
      setSelections({});
      feedbackStartedTracked.current = false;
      return;
    }
    feedbackStartedTracked.current = false;
    setSelections((prev) => {
      const next: Record<number, FeedbackSelection> = {};
      for (const q of questions) {
        next[q.twFeedbackQuestionId] = prev[q.twFeedbackQuestionId] ?? { twFeedbackOptionIds: [] };
      }
      return next;
    });
  }, [questions]);

  const allAnswered = useMemo(() => {
    if (!questions.length) return false;
    return questions.every((q) => {
      const selected = selections[q.twFeedbackQuestionId]?.twFeedbackOptionIds ?? [];
      if (!selected.length) return false;
      const hasTextInputSelected = q.options.some(
        (o) => o.isTextInput && selected.includes(o.twFeedbackOptionId)
      );
      if (hasTextInputSelected && !selections[q.twFeedbackQuestionId]?.optionText?.trim()) {
        return false;
      }
      return true;
    });
  }, [questions, selections]);

  const getFeedbackAnswerSubmitFields = (
    sortedQuestions: FeedbackQuestion[],
    selections: Record<number, FeedbackSelection>
  ): Record<string, string> =>
    sortedQuestions.reduce<Record<string, string>>((fields, question) => {
      const selectedOptionIds = selections[question.twFeedbackQuestionId]?.twFeedbackOptionIds;
      const enOption =
        selectedOptionIds == null
          ? ''
          : question.options.find((o) => selectedOptionIds.includes(o.twFeedbackOptionId))?.enOption ?? '';
      fields[question.enQuestion.toLowerCase()] = enOption.toLowerCase();
      return fields;
    }, {});

  const submitDatalayerEvent = (key: string) => {
    switch (key) {
      case 'page_view':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'page_view',
          'page_name': 'community survey page',
          'page_type': 'survey page',
          ...dataLayerExternalKeys,
          ...dataLayerPoints,
        });
        break;
      case 'feedback_started':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'mission_started',
          'mission_name': 'community survey',
          'mission_type': 'survey',
          ...dataLayerPoints,
        });
        break;
      case 'feedback_completed':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'answer_submit',
          ...getFeedbackAnswerSubmitFields(questions, selections),
          'place_of_question': 'community survey',
          'flow_type': ''
        });
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'community_survey_submitted'
        });
        break;
      case 'feedback_success_modal':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'page_view',
          'page_name': 'community survey completed page',
          'page_type': 'survey page',
          ...dataLayerExternalKeys,
          ...dataLayerPoints,
        });
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'mission_completed',
          'mission_name': 'community survey',
          'mission_type': 'survey',
        });
        break;
      case 'feedback_mission_return_cta_click':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'cta_click',
          'cta_name': 'return to task',
          'cta_type': 'button',
          'cta_position': 'body'
        });
        break;
      case 'feedback_back_to_rewards_cta_click':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'cta_click',
          'cta_name': 'return to rewards',
          'cta_type': 'button',
          'cta_position': 'body'
        });
        break;
      default:
        break;
    }
  }

  const handleSelect = useCallback((questionId: number, optionIds: number[]) => {
    setSelections((prev) => {
      if (!feedbackStartedTracked.current && !Object.values(prev).some((v) => v.twFeedbackOptionIds.length > 0)) {
        feedbackStartedTracked.current = true;
        submitDatalayerEvent('feedback_started');
      }
      const question = questions.find((q) => q.twFeedbackQuestionId === questionId);
      const hasTextInputSelected = question?.options.some(
        (o) => o.isTextInput && optionIds.includes(o.twFeedbackOptionId)
      ) ?? false;
      return {
        ...prev,
        [questionId]: {
          ...prev[questionId],
          twFeedbackOptionIds: optionIds.map((id: number) => Number(id)),
          ...(hasTextInputSelected ? {} : { optionText: undefined }),
        },
      };
    });
  }, [questions, submitDatalayerEvent]);

  const handleOptionTextChange = useCallback((questionId: number, value: string) => {
    setSelections((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        twFeedbackOptionIds: prev[questionId]?.twFeedbackOptionIds ?? [],
        optionText: value,
      },
    }));
  }, []);

  const submit = async () => {
    if (!allAnswered) {
      errorToast(t("feedback.answerRequired"));
      return;
    }
    submitDatalayerEvent('feedback_completed');
    const response = await submitFeedbackMutation.mutateAsync({
      memberId: loggedInDetails?.memberId,
      campaignMasterId: campaignMasterId,
      missionMasterId: missionMasterId,
      answers: Object.entries(selections).map(([questionId, answer]) => {
        const qId = Number(questionId);
        const question = questions.find((q) => q.twFeedbackQuestionId === qId);
        const hasTextInputSelected = question?.options.some(
          (o) => o.isTextInput && answer.twFeedbackOptionIds.includes(o.twFeedbackOptionId)
        ) ?? false;
        return {
          twFeedbackQuestionId: qId,
          twFeedbackOptionIds: answer.twFeedbackOptionIds,
          ...(hasTextInputSelected ? { optionText: answer.optionText?.trim() ?? '' } : {}),
        };
      }),
    });
    if (response?.success) {
      setPointsAwarded(response?.data?.pointsAwarded ?? 0);
      setLineUrl(response?.data?.linePointUrl ?? '');
      setSuccessPopup(true);
      submitDatalayerEvent('feedback_success_modal');
    } else {
      errorToast(response.message ?? 'Error in submitting feedback');
      storeErrorLog({ message: response.message, stack: 'submit feedback' }, loggedInDetails?.memberId);
    }
  };

  return (
    <>
      <PageHeader title={'填寫飲用心得'} />
      <div className="flex min-h-[calc(100dvh-3.5rem)] flex-col green-yellow-gradiant-bg">
        <div
          id="feedback-form"
          className={`border-(--button-green) p-3 ${noData ? "flex flex-1 items-center justify-center" : ""}`}
        >
          {isFeedbackLoading || isSubmittingFeedback ? (
            <div className="space-y-3 px-1 py-2 w-full">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={120} borderRadius={12} />
              ))}
            </div>
          ) : noData ? (
            <div className="flex w-full max-w-sm flex-col items-center justify-center rounded-xl bg-white px-4 py-15 text-center shadow-sm">
              <img
                src={EmptyIcon}
                alt=""
                className="mx-auto mb-10 h-14 w-14 opacity-40"
              />
              <p className="text-(--dark-gray) opacity-60">
                {t("feedback.noDataFound")}
              </p>
            </div>
          ) : (
            <>
              {questions.map((q, index) => (
                <FeedbackQuestionBlock
                  key={q.twFeedbackQuestionId}
                  questionId={q.twFeedbackQuestionId}
                  questionBlockKey={index}
                  question={q.twQuestion}
                  options={q.options}
                  questionType={q.questionType}
                  selectedOptionIds={selections[q.twFeedbackQuestionId]?.twFeedbackOptionIds ?? null}
                  optionText={selections[q.twFeedbackQuestionId]?.optionText ?? ''}
                  onSelectOption={handleSelect}
                  onOptionTextChange={handleOptionTextChange}
                />
              ))}
              <div className="p-3">
                <Button
                  label={t("feedback.submit")}
                  classNames="w-full"
                  onClick={submit}
                  disabled={!allAnswered}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {successPopup ? (
        <SuccessPopup
          content="feedback"
          pageTitle="填寫飲用心得"
          heading={t("feedback.title")}
          subtitle={t("feedback.subtitle")}
          rewardAmount={pointsAwarded}
          lineUrl={lineUrl}
          // footerText={t("feedback.footerText")}
          primaryLabel={t("feedback.backToRewards")}
          // secondaryLabel={t("feedback.backToRewards")}
          dataLayerSubmit={submitDatalayerEvent}
        />
      ) : null}
    </>
  );
};

export default withRouter(Feedback);
